import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { Add, Remove, ReturnUpBack, TrashOutline } from 'react-ionicons';

import { ICartItem } from 'screens/AccessExpressV3/CartProvider';
import ProductInfo from '../ProductInfo';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import Tag from 'components/Tag/Tag';
import { CartItemImage } from './CartItemImage';
import Divider from 'components/Divider';
import {
  getAccessProductSummaryItems,
  getMobilityProductSummaryItems,
  getTotalMRCAndNRC,
} from 'screens/AccessExpressV3/utils';
import { motion } from 'framer-motion';
import CartItemDetails from './CartItemDetails';

interface CartItemProps {
  item: ICartItem;
  onEdit?: () => void;
  onDelete?: () => void;
  onUndoDelete?: () => void;
  onQuantityChange?: (newQuantity: number) => void;
  className?: string;
  variant?: 'default' | 'deleted';
}

const CartItem = observer(
  ({
    item,
    onEdit,
    onDelete,
    onQuantityChange,
    className,
    onUndoDelete,
  }: CartItemProps) => {
    const isFixedQuantity =
      item.subtype === 'broadband' ||
      item.subtype === 'dia' ||
      item.subtype === 'starlink';

    const { mrc, nrc } = getTotalMRCAndNRC(item);

    const [openedDetails, setOpenedDetails] = useState(false);

    return (
      <div className={'flex flex-col gap-6 overflow-hidden'}>
        <div className="flex flex-col">
          <div
            className={clsx(
              'rounded bg-background-ae-cart-item p-2',
              className,
            )}
          >
            <div className="group/cart-item relative z-10 flex flex-row flex-wrap items-stretch justify-between">
              <div className="pointer-events-none relative z-10 flex max-w-[245px] flex-grow flex-col gap-2 ">
                <div className="flex flex-row">
                  <Tag
                    key={item.category}
                    content={{ type: 'label', label: item.category }}
                    status="custom"
                    className={clsx('bg-opacity-[8%]', {
                      'bg-[#9FF4A9] text-[#9FF4A9]': item.category === 'Access',
                      'bg-[#6C9DFF] text-[#6C9DFF]':
                        item.category === 'Mobility',
                      'bg-[#8989FF] text-[#8989FF]': item.category === 'Voice',
                      'bg-[#78CFDA] text-[#78CFDA]': item.category === 'MNS',
                      '!bg-red-400/[8%] !text-red-400':
                        onUndoDelete !== undefined,
                    })}
                  />
                </div>
                <div className="flex gap-3">
                  <div>
                    <CartItemImage item={item} />
                  </div>
                  <ProductInfo
                    name={item.name}
                    price={mrc}
                    secondPrice={nrc}
                    openedDetails={openedDetails}
                    className={!onUndoDelete ? 'text-content-base-default' : ''}
                    id={item.item_details?.id}
                  />
                </div>
              </div>
              <CartActions
                onEdit={onEdit}
                onDecrement={() => {
                  onQuantityChange?.(item.quantity - 1);
                }}
                onIncrement={() => {
                  onQuantityChange?.(item.quantity + 1);
                }}
                onQuantityChange={onQuantityChange}
                readOnly={isFixedQuantity || onQuantityChange === undefined}
                onDelete={onDelete}
                quantity={item.quantity}
                onUndoDelete={onUndoDelete}
                disabled={item.category === 'Mobility' && item.id === undefined}
              />
              <div className="w-full basis-full">
                <div
                  className={clsx(
                    'absolute -inset-x-2 -bottom-2 h-20 rounded rounded-t-none transition-opacity duration-300 ease-in-out',
                    {
                      '!opacity-0': openedDetails,
                      'opacity-0 group-hover/cart-item:opacity-100':
                        !openedDetails,
                    },
                  )}
                  style={{
                    background:
                      'linear-gradient(0deg, rgb(var(--navigation-surface-primary-active) / 1) 0%, rgba(255, 255, 255, 0) 99.4%)',
                  }}
                ></div>
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{
                    height: openedDetails ? 'auto' : 0,
                    opacity: openedDetails ? 1 : 0,
                  }}
                  transition={{
                    opacity: { duration: 0.4, ease: 'easeInOut' },
                    height: { duration: 0.4, ease: 'easeInOut' },
                  }}
                  className="relative overflow-hidden"
                >
                  <Divider className="my-2" variant="subdued" />
                  <div className="px-2 text-xs">
                    {item.category === 'Mobility' ? (
                      <CartItemDetails
                        description={getMobilityProductSummaryItems(item)}
                      />
                    ) : item.category === 'Access' ? (
                      <CartItemDetails
                        description={getAccessProductSummaryItems(item)}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </motion.div>
                <div
                  className={clsx(
                    'absolute inset-x-0 top-0 h-full cursor-pointer',
                  )}
                  onClick={() => setOpenedDetails(!openedDetails)}
                  // style={{ background: 'rgba(0,255,0,0.5)' }}
                ></div>
              </div>
            </div>
          </div>
          {item.outOfStock && <ShippingStatus />}
        </div>
      </div>
    );
  },
);

export default CartItem;

interface QuantityCounterProps {
  onIncrement: () => void;
  onDecrement: () => void;
  quantity: number;
  onQuantityChange?: (newQuantity: number) => void;
  readOnly: boolean;
}

const QuantityCounter = ({
  onIncrement,
  onDecrement,
  quantity,
  onQuantityChange,
  readOnly,
}: QuantityCounterProps) => {
  const [value, setValue] = useState(quantity);

  useEffect(() => setValue(quantity), [quantity]);

  return (
    <div
      className={clsx(
        'flex h-6 w-full rounded border border-button-stroke-secondary-default',
        readOnly
          ? 'border-button-stroke-secondary-disabled bg-button-background-secondary-disabled text-button-content-tertiary-disabled'
          : 'border-button-stroke-secondary-default bg-button-background-secondary-default text-button-content-secondary-default',
      )}
    >
      <div
        className={clsx(
          'text-inherit border-inherit flex h-full w-6 flex-none items-center justify-center border-r',
          !readOnly && 'cursor-pointer',
        )}
        {...(!readOnly && {
          onClick: onDecrement,
        })}
      >
        <Remove color="currentColor" width="12px" height="12px" />
      </div>
      <GraniteInput
        innerInputClassName={clsx(
          '!min-h-[22px] w-full !px-0 !text-xs !text-center !bg-transparent !border-none',
          readOnly
            ? '!text-button-content-tertiary-disabled'
            : '!text-button-content-secondary-default',
        )}
        // readOnly={readOnly}
        readOnly={true}
        value={value === 0 ? '' : value}
        maxLength={2}
        minLength={1}
        onKeyDown={(e) => {
          if (
            !/[0-9]/.test(e.key) &&
            !['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(
              e.key,
            )
          ) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          const value = e.target.value;
          setValue(Number(value));
          if (Number(value) !== 0) onQuantityChange?.(Number(value));
        }}
        onBlur={() => {
          if (value === 0) {
            setValue(quantity);
            onQuantityChange?.(quantity);
          }
        }}
      />
      <div
        className={clsx(
          'border-inherit flex h-full w-6 flex-none items-center justify-center border-l',
          !readOnly && 'cursor-pointer',
        )}
        {...(!readOnly && {
          onClick: onIncrement,
        })}
      >
        <Add color="currentColor" width="12px" height="12px" />
      </div>
    </div>
  );
};

interface CartActionsProps {
  onEdit?: () => void;
  onDecrement: () => void;
  onIncrement: () => void;
  onDelete?: () => void;
  quantity: number;
  onQuantityChange?: (newQuantity: number) => void;
  readOnly: boolean;
  onUndoDelete?: () => void;
  disabled?: boolean;
}

const CartActions = ({
  onEdit,
  onDecrement,
  onIncrement,
  onDelete,
  quantity,
  onQuantityChange,
  readOnly,
  onUndoDelete,
  disabled = false,
}: CartActionsProps) => {
  return (
    <div className="flex-non relative z-10 flex w-[72px] flex-col items-end justify-between">
      <div className="flex h-6 items-center gap-2">
        {onEdit && (
          <GraniteButton
            size="small"
            variant="ghost"
            onClick={onEdit}
            className="hover:!border-accent-primary hover:!text-accent-primary"
            disabled={disabled}
          >
            Edit
          </GraniteButton>
        )}
        {onDelete && (
          <GraniteButton
            size="small"
            variant="unstyled"
            onClick={onDelete}
            className={clsx('!p-0', { '!text-red-400': !disabled })}
            disabled={disabled}
          >
            <TrashOutline color="currentColor" width="16px" height="16px" />
          </GraniteButton>
        )}
      </div>
      {onQuantityChange && (
        <QuantityCounter
          onDecrement={onDecrement}
          onIncrement={onIncrement}
          onQuantityChange={onQuantityChange}
          quantity={quantity}
          readOnly={readOnly || disabled}
        />
      )}
      {onUndoDelete && (
        <div className="flex h-[64px] items-center">
          <GraniteButton
            size="small"
            variant="secondary"
            onClick={onUndoDelete}
            className="relative -top-4 w-full"
          >
            Undo{' '}
            <ReturnUpBack color="currentColor" width="18px" height="18px" />
          </GraniteButton>
        </div>
      )}
    </div>
  );
};

const ShippingStatus = () => (
  <div className="flex items-center gap-2 rounded-bl rounded-br p-2">
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.7408 4.31191L9.74084 1.56191C9.66701 1.5213 9.58411 1.5 9.49984 1.5C9.41558 1.5 9.33268 1.5213 9.25884 1.56191L4.25884 4.31191C4.18045 4.35508 4.11508 4.41849 4.06955 4.49553C4.02402 4.57257 4 4.66042 4 4.74991C4 4.83941 4.02402 4.92726 4.06955 5.0043C4.11508 5.08134 4.18045 5.14475 4.25884 5.18791L8.99984 7.79541V13.1549L7.48184 12.3199L6.99984 13.1954L9.25884 14.4379C9.33268 14.4785 9.41558 14.4998 9.49984 14.4998C9.58411 14.4998 9.66701 14.4785 9.74084 14.4379L14.7408 11.6879C14.8193 11.6448 14.8847 11.5814 14.9302 11.5043C14.9758 11.4273 14.9998 11.3394 14.9998 11.2499V4.74991C14.9998 4.66041 14.9758 4.57255 14.9302 4.4955C14.8847 4.41846 14.8193 4.35506 14.7408 4.31191ZM9.49984 2.57091L13.4623 4.74991L9.49984 6.92891L5.53734 4.74991L9.49984 2.57091ZM13.9998 10.9544L9.99984 13.1544V7.79491L13.9998 5.59491V10.9544Z"
        fill="#6C9DFF"
      />
      <path d="M5 8H1V7H5V8ZM6 12H2V11H6V12ZM7 10H3V9H7V10Z" fill="#6C9DFF" />
    </svg>
    <span className="text-xs font-medium text-[#6b9dfe]">
      Will ship once restocked
    </span>
  </div>
);
